import React, { useEffect, useState } from "react";
import {
  Button,
  Toolbar,
} from "@progress/kendo-react-buttons";
import {
  Signature,
  SignatureChangeEvent,
} from "@progress/kendo-react-inputs";
import { Input } from '@progress/kendo-react-inputs'
import { InputChangeEvent } from '@progress/kendo-react-inputs';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { InputCtlViewMode, PageViewTypeEnum } from '../../models/enums';
import "./SignatureCtl.css";

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type SignatureProps = PropsFromRedux & {
  keyProp?:any;
  mode?: InputCtlViewMode;
  readOnlyAfterSave: boolean;
  value?: string | null;
  textValue?: string|null;
  onUpdate?: (e: string | null, setViewMode?: (newMode: InputCtlViewMode) => void) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onFocus?: (callback: any|undefined) => void;
  barcodeCallback?: any;
  eSigButtonVisible?: boolean;
  eSignatureCallback?:any;
}

interface tcbObjSignature {
  TextValue: string,
  SigValue: string
}


function SignatureCtl(this: any, props: SignatureProps) {
  const [sigValue, setSigValue] = useState<string>();
  const [textValue, setTextValue] = useState<string>();
  const [origSigValue, setOrigSigValue] = useState<string>();
  const [origTextValue, setOrigTextValue] = useState<string>();
  const [mode, setMode] = useState<InputCtlViewMode>(props.mode ?? InputCtlViewMode.ReadOnly);
  const strokeWidth = 3;

  const setDirtyByValues = (nextSig?: string, nextText?: string) => {
    if (!props.onDirtyChange || mode !== InputCtlViewMode.Edit) {
      return;
    }

    const currentSig = nextSig ?? '';
    const currentText = nextText ?? '';
    const originalSig = origSigValue ?? '';
    const originalText = origTextValue ?? '';

    props.onDirtyChange(currentSig !== originalSig || currentText !== originalText);
  }

  useEffect(() => {
    // If a parent explicitly sets `mode`, honour it and don't override based on value/readOnlyAfterSave
    if (props.mode !== undefined) {
      setMode(props.mode);
    }

    if (props.value) {
      let obj: tcbObjSignature;
      try {
        obj = (typeof props.value === 'string') ? JSON.parse(props.value) : (props.value as any);
      }
      catch (e) {
        // If parsing fails, fallback to treating value as raw string container
        obj = { TextValue: '', SigValue: undefined } as any;
      }

      setTextValue(obj?.TextValue);
      setOrigTextValue(obj?.TextValue);

      if (obj?.SigValue) {
        setSigValue(obj.SigValue);
        setOrigSigValue(obj.SigValue);
      } else {
        setSigValue(undefined);
        setOrigSigValue(undefined);
      }

      if (props.readOnlyAfterSave)
        setMode(InputCtlViewMode.ReadOnly);
      else
        setMode(InputCtlViewMode.Edit);
    }
    else {
      setSigValue(undefined);
      setOrigSigValue(undefined);
      setTextValue(undefined);
      setOrigTextValue(undefined);
      setMode(InputCtlViewMode.Edit);
    }

    if (props.onDirtyChange) {
      props.onDirtyChange(false);
    }
  }, [props.value, props.readOnlyAfterSave]);

  

  useEffect(() => {
    if (props.textValue !== undefined && props.textValue !== null) {
      setTextValue(props.textValue);
      setDirtyByValues(sigValue, props.textValue);
    }
  }, [props.textValue]);

  // Keep internal mode in sync when a parent explicitly controls it
  useEffect(() => {
    if (props.mode !== undefined) {
      setMode(props.mode);
    }
  }, [props.mode]);

  const onChange = (e: SignatureChangeEvent) => {
    const newSig = e.value;
    setSigValue(newSig);
    setDirtyByValues(newSig, textValue);
  };


  const onSave = () => {

    setMode(InputCtlViewMode.Saving);

    if (sigValue && textValue) {

      let output: any = {
        "TextValue": textValue,
        "SigValue": sigValue
      };

      let json = JSON.stringify(output);

      if (props.onUpdate && json) {
        props.onUpdate(json, setViewMode);
      }

    } else {

    }
  };

  const onClear = () => {

    setMode(InputCtlViewMode.Saving);

    setTextValue(undefined);

    setSigValue(undefined);

    const textField = document.getElementById("sigtextField") as HTMLInputElement | null;
    if (textField !== null){
      textField.value = "";
    }

    if (props.onUpdate) {
      props.onUpdate(null, setViewMode);
    }
    if (props.onDirtyChange) {
      props.onDirtyChange(false);
    }
  };

  const onEsign = () => {

   setMode(InputCtlViewMode.Saving);

   if (props.userInf.eSignature){

      let obj: tcbObjSignature = (typeof props.userInf.eSignature === 'string') ? JSON.parse(props.userInf.eSignature) : props.userInf.eSignature;
      const textField = document.getElementById("sigtextField") as HTMLInputElement | null;
      if (textField !== null){
        textField.value = obj.TextValue;
      }

      setTextValue(obj.TextValue);

      setSigValue(obj.SigValue);

    if (props.onUpdate) {
      let output: any = {
        "TextValue": obj.TextValue,
        "SigValue": obj.SigValue
      };

      let json = JSON.stringify(output);

      props.onUpdate(json, setViewMode);
    }
   }  
  };

  const onFocus = () => {
    if (props.onFocus)
        props.onFocus(scanCallback);
}

const scanCallback = (e: string) =>
  {
      setTextValue(e);
      setDirtyByValues(sigValue, e);
  }

  const setViewMode = (e: InputCtlViewMode) => {
    setMode(e);
    if (e !== InputCtlViewMode.Edit && props.onDirtyChange) {
      props.onDirtyChange(false);
    }
  }
 
  const updateTextValue = (e:InputChangeEvent) => {

    const nextText = e.value ?? '';
    setTextValue(nextText)
    setDirtyByValues(sigValue, nextText);
  }

  let popupScale = 3;
  if (props.pageInf.pageViewMode === PageViewTypeEnum.Mobile)
    popupScale = 1.2;
  const isDataUrl = (s?: string) => !!s && typeof s === 'string' && s.startsWith('data:');

  if (mode === InputCtlViewMode.ReadOnly) {
    return (
      <div className="SignatureCtl">
        {sigValue && isDataUrl(sigValue) ? (
          <img className="sig-image" src={sigValue} alt={textValue ?? 'signature'} />
        ) : (
          <Signature
            value={sigValue}
            strokeWidth={strokeWidth}
            smooth={true}
            maximizable={true}
            hideLine={true}
            readOnly={true}
            popupScale={popupScale}
          />
        )}
        <Input
          id="sigtextField"
          value={textValue}
          readOnly={true}
        />
        
      </div>
    );
  }

  // Edit / Saving / View modes
  const signatureValue = isDataUrl(sigValue) ? undefined : sigValue;
  return (
    <div className="SignatureCtl">
      <Signature
        value={signatureValue}
        onChange={onChange}
        strokeWidth={strokeWidth}
        smooth={true}
        maximizable={true}
        hideLine={true}
        popupScale={popupScale}
      />
      <Input
        id="sigtextField"
        value={textValue}
        placeholder="Print Name"
        onFocus={onFocus}
        onChange={updateTextValue}
      />
      <Toolbar>
        <Button
          onClick={onEsign}
          disabled={mode !== InputCtlViewMode.Edit}
          hidden={!(props.eSigButtonVisible && props.userInf.eSignAuthorised) || mode === InputCtlViewMode.View}
        >
          e-Sign
        </Button>
        <Button
          icon="save"
          disabled={!sigValue || !textValue}
          onClick={onSave}
          hidden={mode !== InputCtlViewMode.Edit}
        >
          Save
        </Button>

        <Button
          onClick={onClear}
          disabled={!sigValue || !textValue}
          hidden={mode !== InputCtlViewMode.Edit}
        >
          Clear
        </Button>
        <div className='saving' hidden={mode !== InputCtlViewMode.Saving}><span className="k-icon k-font-icon k-i-loading" /></div>
      </Toolbar>
      
    </div>
  );

}
export default connector(SignatureCtl);

